import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AdminShell from './AdminShell'
import Toggle from '../../components/Toggle'
import { tournamentApi } from '../../services/api'

/**
 * Reseller accounts list. Phase 1 = read-only list.
 * Future phases: account detail page (suspend, edit, fee overrides).
 */
export default function ResellerAccounts() {
  const navigate = useNavigate()
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showClosed, setShowClosed] = useState(false)
  const [showChildren, setShowChildren] = useState(false)
  const [remindingId, setRemindingId] = useState(null)
  const [remindMsg, setRemindMsg] = useState('')

  async function sendCardReminder(a, e) {
    e.stopPropagation()
    if (!confirm(`Email ${a.ContactEmail || a.OwnerEmail || 'this reseller'} a reminder to add a payment card on file?`)) return
    setRemindingId(a.ID_NO); setRemindMsg('')
    try {
      const { data } = await tournamentApi.post(`/admin/resellers/accounts/${a.ID_NO}/card-reminder`, {})
      setRemindMsg(data.emailSent ? `✓ Card reminder sent to ${data.sentTo}` : `⚠ Email failed: ${data.emailError || 'unknown'}`)
    } catch (err) {
      setRemindMsg(`⚠ ${err?.response?.data?.message || err.message}`)
    } finally { setRemindingId(null) }
  }

  const visible = useMemo(
    () => accounts.filter(a => {
      if (!showClosed && a.Status === 'closed') return false
      if (!showChildren && a.ParentReseller_ID) return false
      return true
    }),
    [accounts, showClosed, showChildren]
  )
  const closedCount = useMemo(
    () => accounts.filter(a => a.Status === 'closed').length,
    [accounts]
  )
  // Child accounts that are hidden only because they're children (respecting
  // the closed filter), so the count matches what toggling would reveal.
  const childCount = useMemo(
    () => accounts.filter(a => a.ParentReseller_ID && (showClosed || a.Status !== 'closed')).length,
    [accounts, showClosed]
  )

  useEffect(() => {
    tournamentApi.get('/admin/resellers/accounts')
      .then(r => setAccounts(r.data.accounts || []))
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }, [])

  // Super-admin per-account toggle for the Margin Inspector tool. Optimistic:
  // flip locally, PATCH, revert on error.
  async function toggleInspector(a, next) {
    setError(null)
    const prev = accounts
    setAccounts(list => list.map(x => x.ID_NO === a.ID_NO ? { ...x, MarginInspectorEnabled: next ? 1 : 0 } : x))
    try {
      await tournamentApi.patch(`/admin/resellers/accounts/${a.ID_NO}`, { marginInspectorEnabled: next })
    } catch (e) {
      setAccounts(prev)
      setError(e?.response?.data?.message || e.message)
    }
  }

  return (
    <AdminShell title="Reseller Accounts">
      {error && <div style={errorBox}>{error}</div>}
      {remindMsg && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>{remindMsg}</div>}

      {!loading && accounts.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Toggle checked={showChildren} onChange={setShowChildren} />
            <span style={{ fontSize: 13, color: '#0f172a', cursor: 'pointer' }} onClick={() => setShowChildren(v => !v)}>
              Show child accounts {childCount > 0 && <span style={{ color: '#94a3b8' }}>({childCount})</span>}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Toggle checked={showClosed} onChange={setShowClosed} />
            <span style={{ fontSize: 13, color: '#0f172a', cursor: 'pointer' }} onClick={() => setShowClosed(v => !v)}>
              Show closed accounts {closedCount > 0 && <span style={{ color: '#94a3b8' }}>({closedCount})</span>}
            </span>
          </div>
        </div>
      )}

      {loading
        ? <div style={empty}>Loading…</div>
        : visible.length === 0
          ? accounts.length === 0
            ? <div style={empty}>No reseller accounts yet. <a href="/admin/reseller-invites" style={link}>Send an invite</a> to get started.</div>
            : <div style={empty}>No active accounts. {closedCount > 0 && <button onClick={() => setShowClosed(true)} style={{ ...link, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Show {closedCount} closed</button>}</div>
          : (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
              <table style={tbl}>
                <thead>
                  <tr style={tr}>
                    <th style={th}>Name</th>
                    <th style={th}>Slug</th>
                    <th style={th}>Owner</th>
                    <th style={th}>Status</th>
                    <th style={th}>Mode</th>
                    <th style={th}>Card</th>
                    <th style={th}>Currency</th>
                    <th style={th}>Custom Domain</th>
                    <th style={th}>Users</th>
                    <th style={th}>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(a => (
                    <tr key={a.ID_NO}
                      style={{ ...tr, cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/reseller-accounts/${a.ID_NO}`)}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span onClick={e => e.stopPropagation()} title={`Margin Inspector: ${a.MarginInspectorEnabled ? 'on' : 'off'} for this reseller`} style={{ display: 'inline-flex', flexShrink: 0 }}>
                            <Toggle checked={!!a.MarginInspectorEnabled} onChange={(v) => toggleInspector(a, v)} />
                          </span>
                          <div>
                            <strong>{a.Name}</strong>
                            {a.ParentReseller_ID && (
                              <div style={{ marginTop: 2 }}>
                                <span style={childBadge}>Child</span>
                                <span style={{ color: '#64748b', fontSize: 12, marginLeft: 6 }}>of {a.ParentName || '—'}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={tdMono}>{a.Slug}</td>
                      <td style={td}>
                        {a.OwnerEmail
                          ? <>{a.OwnerFirstName} {a.OwnerLastName}<br/><span style={{ color: '#64748b', fontSize: 12 }}>{a.OwnerEmail}</span></>
                          : <span style={{ color: '#94a3b8' }}>—</span>}
                      </td>
                      <td style={td}><StatusBadge value={a.Status} /></td>
                      <td style={td}>{a.PaymentMode ? `Mode ${a.PaymentMode}` : <span style={{ color: '#94a3b8' }}>unset</span>}</td>
                      <td style={td}>
                        {a.PaymentMode === 'B'
                          ? (Number(a.HasVaultCard)
                              ? <span style={{ color: '#0f766e', fontWeight: 600 }}>✓ card</span>
                              : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ color: '#b91c1c', fontWeight: 600 }}>no card</span>
                                  <button onClick={e => sendCardReminder(a, e)} disabled={remindingId === a.ID_NO}
                                    style={{ fontSize: 11, padding: '2px 8px', border: '1px solid #cbd5e1', borderRadius: 5, background: '#fff', cursor: remindingId === a.ID_NO ? 'wait' : 'pointer', color: '#0f172a' }}>
                                    {remindingId === a.ID_NO ? 'Sending…' : 'Remind'}
                                  </button>
                                </span>)
                          : <span style={{ color: '#94a3b8' }}>n/a</span>}
                      </td>
                      <td style={tdMono}>{a.Currency}</td>
                      <td style={td}>
                        {a.CustomDomain ? <code style={{ fontSize: 12 }}>{a.CustomDomain}</code> : <span style={{ color: '#94a3b8' }}>default subdomain</span>}
                        {a.CustomDomain && <div style={{ fontSize: 11, color: '#64748b' }}>{a.CustomDomainStatus}</div>}
                      </td>
                      <td style={td}>{a.UserCount}</td>
                      <td style={td}>{new Date(a.DOE).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      }
    </AdminShell>
  )
}

function StatusBadge({ value }) {
  const colors = {
    pending:   { bg: '#fef3c7', fg: '#92400e' },
    active:    { bg: '#dcfce7', fg: '#166534' },
    suspended: { bg: '#fee2e2', fg: '#991b1b' },
    closed:    { bg: '#e2e8f0', fg: '#475569' },
  }
  const c = colors[value] || colors.closed
  return <span style={{ padding: '3px 8px', background: c.bg, color: c.fg, borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{value}</span>
}

const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const empty = { padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }
const link = { color: 'var(--brand-600)', fontWeight: 600 }
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 14 }
const tr = { borderBottom: '1px solid #f1f5f9' }
const th = { padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase', background: '#f8fafc' }
const td = { padding: '14px 16px', verticalAlign: 'top' }
const tdMono = { ...td, fontFamily: 'monospace', fontSize: 13 }
const childBadge = { padding: '1px 6px', background: '#e0e7ff', color: '#3730a3', borderRadius: 999, fontSize: 10, fontWeight: 700 }
